import {useEffect, useState} from 'react';
import type {StoreOtherAccrualsBreakdown} from '@ozon-ai/ozon/contracts';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog.tsx';
import {ScrollArea} from '@/components/ui/scroll-area.tsx';
import {api} from '../../../api/client';
import {LoadingSpinner} from '../../../components/LoadingSpinner';
import {formatFlow} from '../unit-economics-page/format-money';

type StoreOtherAccrualsDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	from: string;
	to: string;
};

export function StoreOtherAccrualsDialog({
	open,
	onOpenChange,
	from,
	to,
}: StoreOtherAccrualsDialogProps) {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [data, setData] = useState<StoreOtherAccrualsBreakdown | null>(null);

	useEffect(() => {
		if (!open || !from || !to) {
			return;
		}

		let cancelled = false;
		setLoading(true);
		setError(null);
		setData(null);

		void api
			.post<StoreOtherAccrualsBreakdown>('/store-economics/other-accruals', {from, to})
			.then((next) => {
				if (!cancelled) setData(next);
			})
			.catch((err) => {
				if (!cancelled) {
					setError(err instanceof Error ? err.message : 'Не удалось загрузить начисления');
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [open, from, to]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col gap-4 overflow-hidden sm:max-w-lg">
				<DialogHeader className="shrink-0">
					<DialogTitle>Прочие начисления</DialogTitle>
					<DialogDescription>
						Операции Ozon, не попавшие в выручку, логистику, продвижение и другие строки waterfall.
					</DialogDescription>
				</DialogHeader>
				{loading ? <LoadingSpinner compact/> : null}
				{error ? <p className="text-sm text-destructive">{error}</p> : null}
				{!loading && data ? (
					<>
						<p className="text-sm font-medium tabular-nums">
							Итого: {formatFlow(data.total, true)}
						</p>
						{data.lines.length === 0 ? (
							<p className="text-sm text-muted-foreground">За период нет прочих начислений</p>
						) : (
							<ScrollArea className="min-h-0 max-h-[calc(100dvh-14rem)]">
								<ul className="flex flex-col gap-2 pr-3">
									{data.lines.map((line) => (
										<li
											key={line.ozonOperationType}
											className="flex items-start justify-between gap-3 text-sm"
										>
											<div className="min-w-0 flex-1">
												<p className="break-all font-medium">{line.ozonOperationType}</p>
												<p className="text-xs text-muted-foreground">
													{line.operationCount} операций
												</p>
											</div>
											<span className="shrink-0 tabular-nums">
												{formatFlow(line.amount, true)}
											</span>
										</li>
									))}
								</ul>
							</ScrollArea>
						)}
					</>
				) : null}
			</DialogContent>
		</Dialog>
	);
}
